import { useEffect, useState } from 'react';
import { getConversionByTeam } from '../services';
import { IConversionByTeamItem, HookState } from '../types';
import { resolveErrorMessage } from './hookUtils';

// Cache de módulo — esta vista global no necesita refetchear en cada render
const moduleCache: Map<string, { data: IConversionByTeamItem[]; cachedAt: string; fetchedAt: number }> = new Map();
const STALE_MS = 15 * 60 * 1000;

export function useConversionByTeam(
	from: string,
	to: string,
	options?: { team?: string },
): HookState<IConversionByTeamItem[]> {
	const [state, setState] = useState<HookState<IConversionByTeamItem[]>>({
		data: null,
		loading: true,
		error: null,
		cachedAt: null,
	});

	const team = options?.team;

	useEffect(() => {
		if (!from || !to) return;

		let cancelled = false;
		const cacheKey = `${from}-${to}-${team ?? ''}`;
		const cached = moduleCache.get(cacheKey);

		if (cached && Date.now() - cached.fetchedAt < STALE_MS) {
			setState({ data: cached.data, loading: false, error: null, cachedAt: cached.cachedAt });
			return;
		}

		setState((prev) => ({ ...prev, loading: true, error: null }));

		getConversionByTeam(from, to, { team })
			.then(({ data, cachedAt }) => {
				if (cancelled) return;
				moduleCache.set(cacheKey, { data, cachedAt: cachedAt ?? '', fetchedAt: Date.now() });
				setState({ data, loading: false, error: null, cachedAt });
			})
			.catch((err) => {
				if (!cancelled) setState({ data: null, loading: false, error: resolveErrorMessage(err), cachedAt: null });
			});

		return () => {
			cancelled = true;
		};
	}, [from, to, team]);

	return state;
}
